// src/pages/ForumTopic.tsx
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { ArrowLeft, MessageSquare, Pin, Lock, AlertTriangle, X, ImagePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import {
  getTopic,
  getTopicReplies,
  getForumCategory,
  createReply,
  updateTopic,
  deleteTopic,
  updateReply,
  deleteReply
} from '@/lib/supabase/forum';
import { ForumTopic as TopicType, ForumReply } from '@/types/forum';
import { ForumReply as ForumReplyComponent } from '../components/forums/ForumReply';
import { useAuth } from '@/contexts/AuthContext';
import TopicSkeleton from '@/components/forums/TopicSkeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import TopicHeader from '@/components/forums/TopicHeader';
import TopicContent from '@/components/forums/TopicContent';
import ReplyList from '@/components/forums/ReplyList';
import ReplyForm from '@/components/forums/ReplyForm';
import { parseLinks } from "@/lib/utils/parseLinks";
import { ReportButton } from '@/components/forums/ReportButton';
import type { Member } from '@/types/user';
import { addNotification } from '@/lib/supabase/notifications';

const ForumTopic: React.FC = () => {
  const { id, clubId } = useParams<{ id: string; clubId?: string }>();
  const navigate = useNavigate();


  const goBack = () => {
  if (clubId) {
    navigate(`/dashboard/clubs/${clubId}`, { replace: true });
  } else {
    navigate('/dashboard/forums', { replace: true });
  }
};
  const { isDarkMode } = useTheme();
  const { user: currentUser } = useAuth();

  const [topic, setTopic] = useState<TopicType | null>(null);
  const [replies, setReplies] = useState<ForumReply[]>([]);
  const [categoryName, setCategoryName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editMedia, setEditMedia] = useState<string | null>(null);
  const [newMedia, setNewMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const insideClub = Boolean(clubId);
  const isTopicAuthor = currentUser?.id === topic?.author.id;

  /* ------------------------------------------------------------------ */
  /* Fetch topic + replies                                              */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchTopic = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const topicData = await getTopic(id);
        if (!topicData) {
          setError('Topic not found');
          return;
        }

        setTopic(topicData);
        setEditTitle(topicData.title);
        setEditContent(topicData.content);
        setEditMedia(topicData.media || topicData.video || null);

        const repliesData = await getTopicReplies(id);
        setReplies(repliesData);

       if (topicData.categoryId && topicData.categoryId !== 'club') {
        const categoryData = await getForumCategory(topicData.categoryId);
        if (categoryData) {
          setCategoryName(categoryData.name);
        }
      }
      } catch (err) {
        console.error('Error fetching topic:', err);
        setError('Failed to load topic');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopic();
  }, [id]);

  /* ------------------------------------------------------------------ */
  /* Create reply                                                       */
  /* ------------------------------------------------------------------ */
  const handleSubmitReply = async (content: string, mentioned: Member[], media?: File) => {
    if (!id || !content.trim()) return;

    setIsSubmitting(true);
    try {
      const newReply = await createReply(content, id, media);
      if (newReply) {
        setReplies(prev => [...prev, newReply]);

        if (topic) {
  setTopic({
    ...topic,
    lastReplyAt: new Date().toISOString(),
    lastReplyAuthorId: newReply.authorId,
    lastReplyAuthor: {
      id: newReply.author.id,
      name: newReply.author.name,
      image: newReply.author.image
    }
    
  });
}
      // send mention notifications
            // DEBUG: who we're going to notify?
      console.log('[Mention] will notify:', mentioned.map(u => u.auth_id));
        // fetch our profile record so we can use its PK for sender_id
const { data: senderProfile, error: senderProfileError } = await supabase
  .from('users')
  .select('id, full_name')
  .eq('auth_id', currentUser.id)
  .single();

if (senderProfileError || !senderProfile?.id) {
  console.error('Could not fetch sender profile id', senderProfileError);
  return;
}
const senderProfileId = senderProfile.id;
const senderName = senderProfile.full_name;


      for (const u of mentioned) {
        try {
          console.log(`[Mention] addNotification → user_id=${u.id}`);
const res = await addNotification({
  type: 'mention',
  content: `${senderName} mentioned you in a reply`,
  user_id: u.id,
sender_id: senderProfileId,

  metadata: { replyId: newReply.id, topicId: id },
});

          console.log('[Mention] notification result:', res);
        } catch (err) {
          console.error('[Mention] addNotification error for', u.auth_id, err);
        }
      }


      }
    } catch (err) {
      console.error('Error creating reply:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Edit topic                                                         */
  /* ------------------------------------------------------------------ */
  const handleEditTopic = async () => {
    if (!id || !editTitle.trim() || !editContent.trim()) return;

    setIsSubmitting(true);
    try {
      // Prepare update data
      const updateData: any = {
        title: editTitle,
        content: editContent
      };
      
      // Handle media changes
      if (removeMedia) {
        updateData.media_url = null;
        updateData.video_url = null;
      } else if (newMedia) {
        // Upload new media
        const isVideo = newMedia.type.startsWith('video/');
        const bucket = isVideo ? 'videos' : 'social-feed';
        
        // Generate unique filename
        const timestamp = Date.now();
        const extension = newMedia.name.split('.').pop()?.toLowerCase() || (isVideo ? 'mp4' : 'jpg');
        const sanitizedName = newMedia.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        const { data: userData } = await supabase.auth.getUser();
const userId = userData?.user?.id;

if (!userId) throw new Error("User not authenticated");

const filePath = `user_directory/${userId}/${timestamp}-${sanitizedName}`;



        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, newMedia, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from(bucket)
          .getPublicUrl(uploadData.path);
        
        if (isVideo) {
          updateData.video_url = publicUrl;
          updateData.media_url = null;
        } else {
          updateData.media_url = publicUrl;
          updateData.video_url = null;
        }
      }
      
      // Update topic with all changes
      const success = await supabase
        .from(clubId ? 'club_forum_topics' : 'forum_topics')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (success) {
        const updatedTopic = success.data;
        setTopic(prev =>
          prev
            ? { 
                ...prev, 
                title: editTitle, 
                content: editContent, 
                media: updateData.media_url !== undefined ? updateData.media_url : prev.media,
                video: updateData.video_url !== undefined ? updateData.video_url : prev.video,
                updatedAt: new Date().toISOString() 
              }
            : null
        );
        setIsEditing(false);
        setNewMedia(null);
        setMediaPreview(null);
        setRemoveMedia(false);
        if (error) setError(null);            // clear stale error
      }
    } catch (err) {
      console.error('Error updating topic:', err);
      setError(err instanceof Error ? err.message : 'Failed to update topic');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Delete topic                                                       */
  /* ------------------------------------------------------------------ */
  const handleDeleteTopic = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      const success = await deleteTopic(id);
     if (success) {
  if (error) setError(null);
  goBack();
}

    } catch (err) {
      console.error('Error deleting topic:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete topic');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Update reply                                                       */
  /* ------------------------------------------------------------------ */
  const handleUpdateReply = async (
    replyId: string,
    content: string,
    mediaUrl?: string | null,
    videoUrl?: string | null
  ): Promise<boolean> => {
    if (!content.trim()) return false;

    setIsSubmitting(true);
    try {
      // Prepare update data
      const updateData: any = {
        content: content.trim()
      };
      
      // Add media fields if they were provided
      if (mediaUrl !== undefined) {
        updateData.media_url = mediaUrl;
      }
      
      if (videoUrl !== undefined) {
        updateData.video_url = videoUrl;
      }
      
      // Update the reply in the database
      const { error: updateError } = await supabase
        .from('forum_replies')
        .update(updateData)
        .eq('id', replyId);
      
      if (updateError) throw updateError;
      
      const success = true;
      
      if (success) {
        // update React state exactly like handleEditTopic does for topics
        setReplies(prev =>
          prev.map(r =>
            r.id === replyId
              ? {
                  ...r,
                  content,
                  isEdited: true,
                  updatedAt: new Date().toISOString(),
                  media: mediaUrl !== undefined ? mediaUrl : r.media,
                  video: videoUrl !== undefined ? videoUrl : r.video
                }
              : r
          )
        );
        // close inline editor if it was open
   
        setError(null);
        
        console.log('Reply updated successfully:', replyId, content);
       return true;
      }
     return false;
    } catch (err) {
      console.error('Error updating reply:', err);
      setError('Failed to update reply');
     return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Delete reply                                                       */
  /* ------------------------------------------------------------------ */
  const handleDeleteReply = async (replyId: string) => {
    try {
      const success = await deleteReply(replyId);
      if (success) {
        if (error) setError(null);            // clear stale error  <-- NEW
        setReplies(prev => prev.filter(reply => reply.id !== replyId));

        if (topic) {
          setTopic({
            ...topic,
            repliesCount: Math.max(0, topic.repliesCount - 1)
          });
        }
      }
    } catch (err) {
      console.error('Error deleting reply:', err);

    }
  };

  /* ------------------------------------------------------------------ */
  /* Render error screen                                                */
  /* ------------------------------------------------------------------ */
if (error === 'Topic not found' || error === 'Failed to load topic') {

    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="h-16 w-16 text-dark-800 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{error}</h2>
          <p className="text-dark-800 mb-6">
            The topic you're looking for doesn't exist or has been removed.
          </p>
          <Button
  onClick={() => {
    setError(null);
    goBack();
  }}
  className="bg-gold-400 hover:bg-gold-500 text-dark-50"
>
  Back
</Button>

        </div>
      </DashboardLayout>
    );
  }

/* ------------------------------------------------------------------ */
/*  MAIN RENDER – shared markup                                       */
/* ------------------------------------------------------------------ */

const PageBody = (
<div className="w-full max-w-7xl mx-auto px-1 sm:px-1 lg:px-1 pt-4 pb-[120px] space-y-6">


    {/* Back button */}
    <div className="mb-4">
      <button
        onClick={goBack}
        className="flex items-center text-dark-800 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back
      </button>
    </div>

    {isLoading ? (
      <TopicSkeleton />
    ) : topic ? (
      <>
       {/* ───── Topic wrapper ───── */}
<div
  className={cn(
    "rounded-xl border overflow-hidden w-full transition-all duration-300",
    isDarkMode 
      ? "bg-dark-200 border-gold-400/40 hover:border-gold-400"
      : "bg-light-100 border-gold-400/40 hover:border-gold-500"
  )}
>
  <TopicHeader
    topic={topic}
    onEditClick={() => setIsEditing(true)}
    onDeleteClick={() => setShowDeleteConfirm(true)}
  />
<TopicContent topic={topic} />




          {(topic.isPinned || topic.isLocked) && (
            <div
              className={cn(
                "px-6 py-3 border-t flex items-center gap-4",
                isDarkMode ? "border-dark-300 bg-dark-300/50"
                           : "border-light-300 bg-light-200/50"
              )}
            >
              {topic.isPinned && (
                <div className="flex items-center text-gold-400">
                  <Pin className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">Pinned Topic</span>
                </div>
              )}
              {topic.isLocked && (
                <div className="flex items-center text-red-400">
                  <Lock className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">Locked Topic</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ───── Replies ───── */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Replies</h2>
            <div className="text-dark-800">
              {topic.repliesCount} {topic.repliesCount === 1 ? "reply" : "replies"}
            </div>
          </div>

          {replies.length > 0 ? (
            <ReplyList
              replies={replies}
              setReplies={setReplies}
              onUpdateReply={handleUpdateReply}
              onDeleteReply={handleDeleteReply}
            />
          ) : (
            <div
              className={cn(
                "p-6 rounded-xl border text-center",
                isDarkMode ? "bg-dark-200 border-dark-300"
                           : "bg-light-200 border-light-300"
              )}
            >
              <p className="text-dark-800">No replies yet. Be the first to reply!</p>
            </div>
          )}
        </div>

        {/* ───── Reply form ───── */}
        {!topic.isLocked && (
          <div
            className={cn(
              "rounded-xl border overflow-visible w-full",
              isDarkMode ? "bg-dark-200 border-dark-300"
                         : "bg-light-200 border-light-300"
            )}
          >
            <div className="p-4 border-b border-dark-300">
              <h3 className="text-lg font-medium text-white">Post a Reply</h3>
            </div>
            <div className="p-4">
              <ReplyForm 
                onSubmit={handleSubmitReply} 
                isSubmitting={isSubmitting} 
              />
            </div>
          </div>
        )}
      </>
    ) : null}
  </div>
);

/* ------------------------------------------------------------------ */
/*  ALL DIALOGS (unchanged)                                           */
/* ------------------------------------------------------------------ */

const Dialogs = (
  <>
    {/* Edit dialog */}
    <Dialog open={isEditing} onOpenChange={setIsEditing}>
      <DialogContent
        className={cn(
          "sm:max-w-[600px]",
          isDarkMode ? "bg-dark-200 border-dark-300"
                     : "bg-light-100 border-light-300"
        )}
      >
        <DialogHeader>
          <DialogTitle className={isDarkMode ? "text-white" : "text-light-900"}>
            Edit Topic
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <label
              className={cn(
                "text-sm font-medium",
                isDarkMode ? "text-dark-800" : "text-light-600"
              )}
            >
              Title
            </label>
            <Input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              className={cn(
                isDarkMode ? "bg-dark-300 border-dark-400 text-white"
                           : "bg-light-200 border-light-300 text-light-900"
              )}
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <label
              className={cn(
                "text-sm font-medium",
                isDarkMode ? "text-dark-800" : "text-light-600"
              )}
            >
              Content
            </label>
            <Textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className={cn(
                "min-h-[200px]",
                isDarkMode ? "bg-dark-300 border-dark-400 text-white"
                           : "bg-light-200 border-light-300 text-light-900"
              )}
            />
          </div>

          {/* Media Management */}
          <div className="space-y-2">
            <label
              className={cn(
                "text-sm font-medium",
                isDarkMode ? "text-dark-800" : "text-light-600"
              )}
            >
              Media
            </label>
            
            {/* Current Media Preview */}
            {!removeMedia && (editMedia || mediaPreview) && (
              <div className="relative rounded-lg overflow-hidden border border-dark-400 mb-4">
                {mediaPreview ? (
                  newMedia?.type.startsWith('video/') ? (
                    <video 
                      src={mediaPreview} 
                      className="max-h-64 w-auto mx-auto" 
                      controls
                    />
                  ) : (
                    <img 
                      src={mediaPreview} 
                      alt="New media preview" 
                      className="max-h-64 w-auto mx-auto" 
                    />
                  )
                ) : editMedia && (
                  topic?.video ? (
                    <video 
                      src={editMedia} 
                      className="max-h-64 w-auto mx-auto" 
                      controls
                    />
                  ) : (
                    <img 
                      src={editMedia} 
                      alt="Current media" 
                      className="max-h-64 w-auto mx-auto" 
                    />
                  )
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (mediaPreview) {
                      setNewMedia(null);
                      setMediaPreview(null);
                    } else {
                      setRemoveMedia(true);
                    }
                  }}
                  className="absolute top-2 right-2 p-1 rounded-full bg-dark-900/80 text-white hover:bg-dark-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            
            {/* Media Actions */}
            <div className="flex flex-wrap gap-2">
              {removeMedia && editMedia && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRemoveMedia(false)}
                  className={cn(
                    isDarkMode ? "border-dark-400 text-white hover:bg-dark-400" : "border-light-300 text-light-900 hover:bg-light-200"
                  )}
                >
                  Restore Original Media
                </Button>
              )}
              
              {!mediaPreview && (removeMedia || !editMedia) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*,video/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        setNewMedia(file);
                        setRemoveMedia(false);
                        
                        // Create preview
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setMediaPreview(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    };
                    input.click();
                  }}
                  className={cn(
                    isDarkMode ? "border-dark-400 text-white hover:bg-dark-400" : "border-light-300 text-light-900 hover:bg-light-200"
                  )}
                >
                  <ImagePlus className="h-4 w-4 mr-2" />
                  {editMedia ? 'Replace Media' : 'Add Media'}
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsEditing(false);
              setNewMedia(null);
              setMediaPreview(null);
              setRemoveMedia(false);
              if (topic) {
                setEditTitle(topic.title);
                setEditContent(topic.content);
                setEditMedia(topic.media || topic.video || null);
              }
            }}
            className={cn(
              isDarkMode ? "border-dark-400 text-white hover:bg-dark-400" : "border-light-300 text-light-900 hover:bg-light-200"
            )}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditTopic}
            disabled={isSubmitting || !editTitle.trim() || !editContent.trim()}
            className="bg-gold-400 hover:bg-gold-500 text-dark-50"
          >
            {isSubmitting ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete confirmation */}
    <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <DialogContent
        className={cn(
          "sm:max-w-[425px]",
          isDarkMode ? "bg-dark-200 border-dark-300"
                     : "bg-light-100 border-light-300"
        )}
      >
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <DialogTitle
            className={cn("text-center", isDarkMode ? "text-white" : "text-light-900")}
          >
            Delete Topic
          </DialogTitle>
          <DialogDescription
            className={cn("text-center", isDarkMode ? "text-dark-800" : "text-light-600")}
          >
            Are you sure you want to delete this topic? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="sm:justify-center">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className={cn(
                "sm:order-1",
                isDarkMode ? "border-dark-400 text-white hover:bg-dark-400" : "border-light-300 text-light-900 hover:bg-light-200"
              )}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDeleteTopic}
              disabled={isDeleting}
              className={cn(
                "bg-red-500 hover:bg-red-600 text-white sm:order-2",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isDeleting ? "Deleting…" : "Delete Topic"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);

/* ------------------------------------------------------------------ */
/*  FINAL OUTPUT – skip DashboardLayout inside clubs                  */
/* ------------------------------------------------------------------ */

return insideClub ? (
  <>
    {PageBody}
    {Dialogs}
  </>
) : (
  <DashboardLayout>
    {PageBody}
    {Dialogs}
  </DashboardLayout>
);

};

export default ForumTopic;